import { useMemo, useRef, useState } from 'react'
import { useSearch } from '@faststore/sdk'
import { InView } from 'react-intersection-observer'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'
import { PickupPointChevronDownIcon } from 'src/components/Icons/PickupPointChevronDownIcon'
import { PickupPointChevronUpIcon } from 'src/components/Icons/PickupPointChevronUpIcon'
import { PickupPointStoreIcon } from 'src/components/Icons/PickupPointStoreIcon'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'

import type { Facet, FacetValue } from '../types'

type PickupFacetValue = FacetValue & {
  distance?: string
  address?: {
    city: string
    state: string
    street: string
    number: string
    neighborhood: string
  }
}

const VISIBLE_COUNT = 4
const RNL_VIEWED_SESSION_KEY = 'pickup-point-filter-rnl-viewed'

function formatStoreName(label: string): string {
  return label.replace(/^loja\s*-\s*/i, '')
}

function hasRnlViewedInSession() {
  if (typeof window === 'undefined') {
    return false
  }

  return window.sessionStorage.getItem(RNL_VIEWED_SESSION_KEY) === 'true'
}

function markRnlViewedInSession() {
  if (typeof window === 'undefined') {
    return
  }

  window.sessionStorage.setItem(RNL_VIEWED_SESSION_KEY, 'true')
}

type PickupPointFilterProps = {
  facet: Facet
}

export function PickupPointFilter({ facet }: PickupPointFilterProps) {
  const { pageData } = useGlobalContext()

  const {
    state: { selectedFacets },
    setFacets,
  } = useSearch()

  const standardObject = useMemo(() => getStandardObject(pageData), [pageData])
  const hasTrackedViewedRef = useRef(hasRnlViewedInSession())

  const [expanded, setExpanded] = useState(false)

  const stores = facet.values as PickupFacetValue[]
  const selectedPickup = selectedFacets.find((f) => f.key === 'pickupPoint')
  const visibleStores = expanded ? stores : stores.slice(0, VISIBLE_COUNT)
  const hiddenCount = stores.length - VISIBLE_COUNT

  const handleSelect = (store: PickupFacetValue) => {
    const withoutPickup = selectedFacets.filter((f) => f.key !== 'pickupPoint')
    const isDeselectingCurrentStore = selectedPickup?.value === store.value

    if (isDeselectingCurrentStore) {
      setFacets(withoutPickup)
    } else {
      if (standardObject) {
        dispatchAmplitudeEvent({
          eventName: 'RNL Store Selected',
          eventData: {
            ...standardObject,
            'rnl store': formatStoreName(store.label),
          },
        })
      }

      setFacets([...withoutPickup, { key: 'pickupPoint', value: store.value }])
    }
  }

  const handleInViewChange = (inView: boolean) => {
    if (
      !inView ||
      !standardObject ||
      hasTrackedViewedRef.current ||
      hasRnlViewedInSession()
    ) {
      return
    }

    dispatchAmplitudeEvent({
      eventName: 'RNL Viewed',
      eventData: {
        ...standardObject,
      },
    })

    hasTrackedViewedRef.current = true
    markRnlViewedInSession()
  }

  return (
    <InView
      as="div"
      triggerOnce
      onChange={handleInViewChange}
      className="flex flex-col gap-3 py-4 border-b border-gray"
      data-testid="pickup-point-filter"
    >
      <div className="flex items-center gap-2">
        <PickupPointStoreIcon className="text-primaryBlue" aria-hidden="true" />
        <span className="text-[14px] font-semibold text-pickupPointTitle leading-[21px]">
          Loja de retirada
        </span>
      </div>

      <ul className="flex flex-col">
        {visibleStores.map((store) => {
          const isSelected = selectedPickup?.value === store.value
          const subtitle = [
            store.address
              ? `${store.address.city}/${store.address.state}`
              : null,
            store.distance ?? null,
          ]
            .filter(Boolean)
            .join(' · ')

          return (
            <li key={store.value}>
              <button
                className={`w-full h-[52px] flex items-center gap-[10px] pl-3 pr-3 text-left transition-colors ${
                  isSelected
                    ? 'bg-pickupPointSelectedBg border border-primaryBlue'
                    : 'rounded-[12px] border border-transparent hover:bg-gray-50'
                }`}
                onClick={() => handleSelect(store)}
                aria-pressed={isSelected}
              >
                <span
                  className={`shrink-0 w-4 h-4 rounded-full border flex items-center justify-center transition-colors ${
                    isSelected
                      ? 'border-primaryBlue bg-primaryBlue'
                      : 'border-pickupPointRadioBorder bg-white'
                  }`}
                >
                  {isSelected && (
                    <span className="w-[6px] h-[6px] rounded-full bg-white" />
                  )}
                </span>
                <span className="flex flex-col">
                  <span
                    className={`capitalize text-[12.5px] font-semibold leading-[15.63px] ${
                      isSelected ? 'text-primaryBlue' : 'text-gray'
                    }`}
                  >
                    {formatStoreName(store.label)}
                  </span>
                  {subtitle && (
                    <span className="text-[11px] text-pickupPointMuted leading-[13.75px]">
                      {subtitle}
                    </span>
                  )}
                </span>
              </button>
            </li>
          )
        })}
      </ul>

      {hiddenCount > 0 && (
        <button
          className="flex items-center gap-1 text-[12.5px] font-semibold text-primaryBlue hover:underline"
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded ? (
            <>
              Ver menos
              <PickupPointChevronUpIcon aria-hidden="true" />
            </>
          ) : (
            <>
              Ver todas ({stores.length})
              <PickupPointChevronDownIcon aria-hidden="true" />
            </>
          )}
        </button>
      )}
    </InView>
  )
}
